import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { ProfileResponse } from "~/api/auth"
import { getProfile } from "~/api/auth"
import { updateDog } from "~/api/dogs"
import { regeneratePortrait } from "~/api/portrait"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"

interface HeroPortraitCardProps {
  profile: ProfileResponse
  onProfileUpdated: (profile: ProfileResponse) => void
}

export function HeroPortraitCard({ profile, onProfileUpdated }: HeroPortraitCardProps) {
  const navigate = useNavigate()
  const dog = profile.dog_profile
  const portrait = dog?.portrait
  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [styleSaving, setStyleSaving] = useState(false)
  const [styleError, setStyleError] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)

  const startPollingForReady = useCallback(() => {
    if (pollRef.current) window.clearInterval(pollRef.current)
    pollRef.current = window.setInterval(async () => {
      try {
        const data = await getProfile()
        onProfileUpdated(data)
        if (data.dog_profile?.portrait?.status === "ready" || data.dog_profile?.portrait?.status === "failed") {
          if (pollRef.current) window.clearInterval(pollRef.current)
        }
      } catch {
        // ignore
      }
    }, 2000)
  }, [onProfileUpdated])

  useEffect(() => {
    if (portrait?.status !== "generating") return
    startPollingForReady()
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
    }
  }, [portrait?.status, startPollingForReady])

  if (!dog || !portrait) return null

  const thumbnailUrl = heroPortraitUrl(dog.archetype_icon, portrait)
  const isPersonalised = portrait.status === "ready" && Boolean(portrait.url)
  const canChange = portrait.can_regenerate && portrait.status !== "generating"
  const hasUnlimitedPhotos = hasEntitlement(
    profile.user.entitlements,
    ENTITLEMENTS.unlimitedPhotoUploads,
  )
  const canSelectSeasonal = hasEntitlement(
    profile.user.entitlements,
    ENTITLEMENTS.seasonalPortraits,
  )
  const seasonalPortraitAvailable = profile.seasonal_portrait_available ?? false
  const selectedStyle = dog.selected_portrait_style || "personality"
  const effectiveStyle =
    selectedStyle === "seasonal" && (!canSelectSeasonal || !seasonalPortraitAvailable)
      ? "personality"
      : selectedStyle

  async function handleConfirmRegenerate() {
    if (!photo) return
    setSubmitting(true)
    setUploadError(null)
    try {
      await regeneratePortrait(photo)
      setConfirming(false)
      setPhoto(null)
      setPreviewUrl(null)
      startPollingForReady()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Could not start portrait generation")
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePortraitStyleChange(styleId: string) {
    const dogId = dog.id ?? profile.active_dog_profile_id
    if (!dogId || styleId === selectedStyle) return
    if (styleId === "seasonal") {
      if (!canSelectSeasonal) {
        navigate("/plus")
        return
      }
      if (!seasonalPortraitAvailable) {
        return
      }
    }
    setStyleSaving(true)
    setStyleError(null)
    try {
      const result = await updateDog(dogId, { selected_portrait_style: styleId })
      onProfileUpdated({
        ...profile,
        dog_profile: {
          ...dog,
          selected_portrait_style: result.dog.selected_portrait_style,
        },
      })
    } catch (err) {
      setStyleError(err instanceof Error ? err.message : "Could not update portrait style")
    } finally {
      setStyleSaving(false)
    }
  }

  return (
    <section className="mt-6 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 shadow-sm">
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Hero Portrait</h2>
      <p className="mt-1 text-sm font-medium text-[var(--color-text-secondary)]">
        {isPersonalised
          ? "Your personalised Doggy Dynamo portrait"
          : `${dog.name}'s Doggy Dynamo portrait`}
      </p>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        {isPersonalised && portrait.has_source_photo
          ? `This portrait was created especially for ${dog.name} using the photo you uploaded.`
          : isPersonalised
            ? `This portrait was created especially for ${dog.name}.`
            : `This is ${dog.name}'s classic personality portrait based on their archetype.`}
      </p>

      {thumbnailUrl && (
        <HeroPortraitFrame
          className="mt-4"
          src={thumbnailUrl}
          alt={isPersonalised ? `Personalised portrait for ${dog.name}` : `Portrait for ${dog.name}`}
        />
      )}

      <div className="mt-4">
        <label
          htmlFor="portrait-style-select"
          className="block text-sm font-medium text-[var(--color-text-primary)]"
        >
          Portrait Style
        </label>
        <select
          id="portrait-style-select"
          className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
          value={effectiveStyle}
          disabled={styleSaving}
          onChange={(event) => void handlePortraitStyleChange(event.target.value)}
        >
          <option value="personality">Personality Portrait</option>
          <option value="seasonal" disabled={!canSelectSeasonal || !seasonalPortraitAvailable}>
            Seasonal Portrait
            {!canSelectSeasonal ? " (Plus)" : !seasonalPortraitAvailable ? " (Coming soon)" : ""}
          </option>
        </select>
        {!seasonalPortraitAvailable && canSelectSeasonal && (
          <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
            Seasonal portraits are coming soon.
          </p>
        )}
        {styleError && (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {styleError}
          </p>
        )}
      </div>

      {portrait.hero_refresh_queued && portrait.status !== "generating" && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
          A scheduled portrait refresh is queued — we&apos;ll update {dog.name}&apos;s portrait soon.
        </p>
      )}

      {portrait.status === "generating" && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
          Creating your new portrait…
        </p>
      )}

      {portrait.status === "failed" && portrait.failure_message && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
          {portrait.failure_message}
        </p>
      )}

      {!confirming ? (
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            disabled={!canChange}
            onClick={() => setConfirming(true)}
            className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Change portrait
          </button>
          {!hasUnlimitedPhotos && !portrait.complimentary_update_used && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              Freemium accounts include one complimentary portrait update.
            </p>
          )}
          {!canChange && portrait.regeneration_blocked_reason && (
            <p className="text-xs text-[var(--color-text-secondary)]">
              {portrait.regeneration_blocked_reason}
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <PhotoUploadPanel
            dogName={dog.name}
            photo={photo}
            previewUrl={previewUrl}
            error={uploadError}
            onPhotoSelected={(file, preview) => {
              setPhoto(file)
              setPreviewUrl(preview)
              setUploadError(null)
            }}
            onClear={() => {
              setPhoto(null)
              setPreviewUrl(null)
            }}
          />
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              disabled={!photo || submitting}
              onClick={handleConfirmRegenerate}
              className="flex-1 rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {submitting ? "Starting…" : "Confirm and generate"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false)
                setPhoto(null)
                setPreviewUrl(null)
                setUploadError(null)
              }}
              className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
